<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  CreateImageStepUpload,
  FormImageStepUploadDataQuery,
  FormImageStepUploadDataQueryVariables,
  ImageExtension,
  ImageOptions,
  PermissionOrganization,
  UpdateImageStepUpload,
} from '@/generated/graphql'
import useVuelidate from '@vuelidate/core'
import TextField from '@/components/input/TextField.vue'
import { caption } from '@/components/stepUpload/stepUploadValidation'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed, ref } from 'vue'
import ViewImageStepUpload from '@/components/stepUpload/ViewImageStepUpload.vue'
import ImageEditorDialog from '@/components/stepUpload/ImageEditorDialog.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import useAuthorization from '@/composables/useAuthorization'
import { DraftImageEdit } from '@/utils/imageEdit'

type Model = CreateImageStepUpload & UpdateImageStepUpload
const model = defineModel<Model>({ required: true })
const props = defineProps<{
  uploadId: string
  addDraft?: (edit: DraftImageEdit | undefined) => Promise<void>
}>()
const emit = defineEmits<{
  imageEdited: []
}>()

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()

const imageOptions: ImageOptions = {
  width: 500,
  height: 500,
}
const fetchQuery = useQuery<FormImageStepUploadDataQuery, FormImageStepUploadDataQueryVariables>(
  gql`
    query FormImageStepUploadData($id: ID!, $options: ImageOptions!) {
      upload {
        upload(id: $id) {
          extension {
            ... on ImageExtension {
              url(options: $options)
            }
          }
        }
      }
    }
  `,
  () => ({
    id: props.uploadId,
    options: imageOptions,
  }),
)
const extension = computed(
  () => fetchQuery.result.value?.upload.upload.extension as ImageExtension | undefined,
)
const imageUrl = computed(() => extension.value?.url)

const validation = useVuelidate<Model>({ caption }, model)

const editorDialog = ref<ComponentExposed<typeof ImageEditorDialog>>()
const canEditImage = computed(() =>
  hasOrgAuthz(PermissionOrganization.OrgProductWrite, PermissionOrganization.OrgUploadWrite),
)

const onImageEdited = () => {
  fetchQuery.refetch()
  emit('imageEdited')
}

defineExpose({ validation })
</script>

<template>
  <div class="text-center position-relative">
    <view-image-step-upload
      v-if="!!imageUrl"
      :src="imageUrl"
      :max-width="imageOptions.width"
      :max-height="imageOptions.height"
      :options="model"
      class="mx-auto mt-3"
    />
    <v-btn
      v-if="canEditImage && !!imageUrl"
      icon="edit"
      density="compact"
      variant="elevated"
      class="image-edit-button"
      :title="t('entity.stepUpload.imageEditor.title')"
      @click="editorDialog?.open()"
    />
  </div>
  <text-field
    v-model="model.caption"
    :validation="validation.caption"
    :label="t('entity.stepUpload.field.caption')"
  />
  <image-editor-dialog
    v-if="canEditImage"
    :id="model.id || undefined"
    ref="editorDialog"
    :draft-upload-id="!model.id ? props.uploadId : undefined"
    :add-draft="props.addDraft"
    @saved="onImageEdited"
  />
</template>

<style scoped lang="scss">
.image-edit-button {
  position: absolute;
  top: 0.75em;
  right: 0.75em;
}
</style>
